import { useActivityPageContext } from "PFApp/activities/show/activity_page_context";
import { useGrowl } from "PFApp/use_growl";
import { getProfileName } from "PFCore/helpers/profile";
import { profileHash } from "PFCore/helpers/profile_hash";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { AddToComparisonButton } from "../../components/add_to_comparison_button";
import { useMatchCardContext } from "../../match_card_context";
import css from "./match_card_shortlist_button_wrapper.module.scss";
import {
  ApprovalButtons,
  BookButton,
  FillAndBookButton,
  FillButton,
  InviteButton,
  ShortlistButton
} from "./shortlist_buttons";
import { ShortlistButtonsLabel } from "./shortlist_buttons/shortlist_buttons_label";
import { useShortlistButtonsVisibility } from "./use_shortlist_buttons_visibility";

export type ShortlistAction = "accept" | "decline" | "fill" | "invite" | "shortlist";

const GROWL_TTL = 3000;

export const MatchCardShortlistButtonWrapper = () => {
  const { role, profile, shortlist } = useMatchCardContext();
  const { tabActiveId, isUnbiasedEnabled, isActivityUnbiased } = useActivityPageContext();
  const { t } = useTranslation("activities");
  const growl = useGrowl();

  const isOnMatchesTab = tabActiveId === "matches";
  const isUnbiased = isUnbiasedEnabled && isActivityUnbiased;

  const {
    displayApprovalButtons,
    displayInviteButton,
    displayFillButton,
    displayFillAndBookButton,
    displayBookButton
  } = useShortlistButtonsVisibility();

  const candidateNameDislay = isUnbiased
    ? t("parts.candidateHash", { profileHash: profileHash(profile.id) })
    : getProfileName(profile);

  const handleShortlistActionSuccess = (action: ShortlistAction) =>
    growl({
      kind: "success",
      message: t(`parts.shortlistActionSuccess.${action}`, { name: candidateNameDislay }),
      ttl: GROWL_TTL
    });

  const handleShortlistActionError = () =>
    growl({ kind: "error", message: t("parts.problemUpdatingShortlist"), ttl: GROWL_TTL });

  const ShortlistActionButton = useMemo(() => {
    if (displayApprovalButtons) {
      return ApprovalButtons;
    }
    if (displayInviteButton) {
      return InviteButton;
    }
    if (displayFillAndBookButton) {
      return FillAndBookButton;
    }
    if (displayFillButton) {
      return FillButton;
    }
    if (displayBookButton) {
      return BookButton;
    }

    return null;
  }, [
    displayApprovalButtons,
    displayBookButton,
    displayFillAndBookButton,
    displayFillButton,
    displayInviteButton
  ]);

  return (
    <>
      <div className={css.labelAndIcons}>
        <ShortlistButtonsLabel isOnMatchesTab={isOnMatchesTab} shortlist={shortlist} />
        <AddToComparisonButton />
      </div>
      {isOnMatchesTab ? (
        <>
          {!shortlist && (
            <ShortlistButton
              roleId={role.id}
              profileId={profile.id}
              onError={handleShortlistActionError}
              onSuccess={handleShortlistActionSuccess}
            />
          )}
          {displayFillAndBookButton && <FillAndBookButton shortlist={shortlist} />}
        </>
      ) : (
        shortlist &&
        ShortlistActionButton && (
          <ShortlistActionButton
            shortlist={shortlist}
            onSuccess={handleShortlistActionSuccess}
            onError={handleShortlistActionError}
          />
        )
      )}
    </>
  );
};
